/**
 * Generic bulk-operation schemas used by master list pages.
 *
 * Bulk-delete reuses the existing BulkDeleteRequestSchema in users.ts.
 * Bulk-set-status is generic across masters that have an `isActive` flag.
 *
 * The response shape is intentionally generous: `updated` is the count of
 * rows successfully changed, and `failed` carries per-row failures so the
 * UI can show "Updated 5 of 7 (2 failed)" instead of a single error toast.
 */
import { z } from 'zod';

import { UuidSchema } from './common.js';

/** `POST /admin/<resource>/bulk-set-status` body. */
export const BulkSetStatusRequestSchema = z.object({
  ids: z.array(UuidSchema).min(1).max(500),
  isActive: z.boolean(),
});
export type BulkSetStatusRequest = z.infer<typeof BulkSetStatusRequestSchema>;

/** Per-row failure record. */
export const BulkOpFailureSchema = z.object({
  id: z.string(),
  error: z.string(),
});
export type BulkOpFailure = z.infer<typeof BulkOpFailureSchema>;

/** Generic bulk-op response - reused by bulk-delete and bulk-set-status. */
export const BulkOpResultSchema = z.object({
  updated: z.number().int().min(0),
  failed: z.array(BulkOpFailureSchema),
});
export type BulkOpResult = z.infer<typeof BulkOpResultSchema>;
